import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const SWIPE_THRESHOLD = 45;
const CLICK_TOLERANCE = 8;

const Viewer360 = ({ vehicle }) => {
  const { t } = useLanguage();
  const gallery = vehicle.gallery && vehicle.gallery.length ? vehicle.gallery : [vehicle.image];

  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const drag = useRef({ startX: 0, active: false, moved: 0 });

  const prev = () => setIndex((i) => (i - 1 + gallery.length) % gallery.length);
  const next = () => setIndex((i) => (i + 1) % gallery.length);

  useEffect(() => {
    gallery.forEach((src) => { const img = new Image(); img.src = src; });
  }, [gallery]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox, gallery.length]);

  const onPointerDown = (e) => {
    drag.current = { startX: e.clientX, active: true, moved: 0 };
  };
  const onPointerMove = (e) => {
    if (drag.current.active) drag.current.moved = e.clientX - drag.current.startX;
  };
  const onPointerUp = () => {
    if (!drag.current.active) return;
    const moved = drag.current.moved;
    drag.current.active = false;
    if (Math.abs(moved) >= SWIPE_THRESHOLD) {
      if (moved < 0) next();
      else prev();
    } else if (Math.abs(moved) <= CLICK_TOLERANCE) {
      setLightbox(true);
    }
  };

  return (
    <>
      <div data-testid="vehicle-viewer" className="relative select-none overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c10]">
        <div
          data-testid="viewer-gallery"
          className="viewer-grab relative aspect-[16/10] w-full"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => { drag.current.active = false; }}
        >
          {gallery.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt={`${vehicle.name} photo ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              draggable={false}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${i === index ? "opacity-100" : "opacity-0"}`}
            />
          ))}
          <span className="pointer-events-none absolute bottom-3 end-3 rounded-full bg-black/60 px-3 py-1.5 text-[10px] font-semibold tracking-widest text-slate-300 backdrop-blur-sm" data-testid="gallery-counter">
            {index + 1} / {gallery.length}
          </span>
          {gallery.length > 1 && (
            <>
              <button
                type="button"
                data-testid="gallery-prev"
                aria-label={t("modal.previous")}
                onClick={prev}
                className="absolute start-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-gold hover:text-[#08080A]"
              >
                <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
              </button>
              <button
                type="button"
                data-testid="gallery-next"
                aria-label={t("modal.next")}
                onClick={next}
                className="absolute end-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-gold hover:text-[#08080A]"
              >
                <ChevronRight className="h-5 w-5 rtl:rotate-180" />
              </button>
              <div className="absolute bottom-3 start-1/2 flex -translate-x-1/2 gap-1.5">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`gallery-dot-${i}`}
                    aria-label={`${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-gold" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95 p-4 sm:p-10"
            onClick={() => setLightbox(false)}
            role="dialog"
            aria-modal="true"
            aria-label={vehicle.name}
          >
            <button
              type="button"
              data-testid="lightbox-close"
              aria-label={t("modal.close")}
              onClick={() => setLightbox(false)}
              className="absolute end-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-gold hover:text-[#08080A]"
            >
              <X className="h-6 w-6" />
            </button>
            <span className="absolute start-4 top-4 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold tracking-widest text-slate-300" data-testid="lightbox-counter">
              {index + 1} / {gallery.length}
            </span>
            <motion.img
              key={gallery[index] + index}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              src={gallery[index]}
              alt={`${vehicle.name} photo ${index + 1} full size`}
              draggable={false}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
              data-testid="lightbox-image"
            />
            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid="lightbox-prev"
                  aria-label={t("modal.previous")}
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute start-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-gold hover:text-[#08080A] sm:start-6"
                >
                  <ChevronLeft className="h-6 w-6 rtl:rotate-180" />
                </button>
                <button
                  type="button"
                  data-testid="lightbox-next"
                  aria-label={t("modal.next")}
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute end-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-gold hover:text-[#08080A] sm:end-6"
                >
                  <ChevronRight className="h-6 w-6 rtl:rotate-180" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Viewer360;
